/**
 * DeviceSettingsContext - Global settings for device app
 * Persists to localStorage for offline access
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const DeviceSettingsContext = createContext();

const DEFAULT_SETTINGS = {
  fontSize: 'large', // 'small' | 'medium' | 'large' | 'xlarge'
  contrast: 'normal', // 'normal' | 'high'
  volume: 0.7, // 0.0 - 1.0
  language: 'de', // 'de' | 'tr'
  microphone: true, // enable/disable voice commands
  ttsEnabled: true, // enable/disable text-to-speech
  
  // P2: Clock settings (Default: medium size, date on, seconds off)
  clock: {
    size: 'medium', // 'small' | 'medium' | 'large'
    position: 'top', // 'top' | 'center'
    showSeconds: false,
    showDate: true
  },
  
  // P2: Screensaver settings (Default: enabled with 5 min timeout)
  screensaver: {
    enabled: true,
    startAfterMinutes: 5,
    photoMode: 'none' // 'none' | 'manual_uploads' | 'household_album' (structure ready for photos)
  }
};

const STORAGE_KEY = 'hayat_device_settings';

export function DeviceSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (error) {
      console.error('[Settings] Failed to load from localStorage:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        console.log('[Settings] Saved to localStorage:', settings);
      } catch (error) {
        console.error('[Settings] Failed to save to localStorage:', error);
      }
    }
  }, [settings, isLoading]);

  // Apply settings to document root
  useEffect(() => {
    if (!isLoading) {
      const root = document.documentElement;
      
      // Font size
      const fontSizeMap = {
        small: '14px',
        medium: '16px',
        large: '18px',
        xlarge: '22px'
      };
      root.style.fontSize = fontSizeMap[settings.fontSize] || '18px';
      
      // Contrast mode
      if (settings.contrast === 'high') {
        root.classList.add('high-contrast');
      } else {
        root.classList.remove('high-contrast');
      }
      
      console.log('[Settings] Applied to DOM:', settings);
    }
  }, [settings, isLoading]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const value = {
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    isLoading
  };

  return (
    <DeviceSettingsContext.Provider value={value}>
      {children}
    </DeviceSettingsContext.Provider>
  );
}

export function useDeviceSettings() {
  const context = useContext(DeviceSettingsContext);
  if (!context) {
    throw new Error('useDeviceSettings must be used within DeviceSettingsProvider');
  }
  return context;
}
